import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Layers, CheckCircle2, ShieldCheck, XCircle } from 'lucide-react';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  featureFlagsService,
  FeatureFlag,
} from '@/features/branding/services/feature-flags.service';

export const Route = createFileRoute('/_authenticated/admin/features')({
  component: FeaturesAdminRoute,
});

function FeaturesAdminRoute() {
  return (
    <PermissionGate permission={Permission.ADMIN_TENANT}>
      <FeaturesAdminPage />
    </PermissionGate>
  );
}

function statusBadge(flag: FeatureFlag) {
  if (flag.isEnabled && flag.source === 'addon') {
    return (
      <Badge className="gap-1 bg-green-600 hover:bg-green-600">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Contratado
      </Badge>
    );
  }
  if (flag.isEnabled && flag.source === 'manual') {
    return (
      <Badge className="gap-1 bg-blue-600 hover:bg-blue-600">
        <ShieldCheck className="h-3.5 w-3.5" />
        Liberado pela plataforma
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 text-muted-foreground">
      <XCircle className="h-3.5 w-3.5" />
      Não contratado
    </Badge>
  );
}

function FeaturesAdminPage() {
  const { data: flags = [], isLoading } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: () => featureFlagsService.list(),
  });

  if (isLoading) return <div className="p-6 text-muted-foreground">Carregando módulos...</div>;

  const enabled = flags.filter((f) => f.isEnabled);
  const disabled = flags.filter((f) => !f.isEnabled);

  return (
    <div className="p-6 max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Layers className="h-6 w-6" /> Módulos do sistema
        </h1>
        <p className="text-muted-foreground mt-1">
          Módulos são liberados conforme os add-ons contratados pela sua empresa. Para contratar
          ou alterar módulos, entre em contato com a equipe da plataforma.
        </p>
      </div>

      <div className="flex gap-2 text-sm text-muted-foreground">
        <Badge variant="default">{enabled.length} ativos</Badge>
        <Badge variant="outline">{disabled.length} inativos</Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Funcionalidades</CardTitle>
          <CardDescription>
            Lista somente leitura. Módulos não contratados ficam invisíveis para os usuários
            desta conta.
          </CardDescription>
        </CardHeader>
        <CardContent className="divide-y">
          {flags.map((flag) => (
            <div key={flag.featureKey} className="flex items-center justify-between py-4 gap-4">
              <div>
                <p className="text-base font-medium">{flag.label}</p>
                <p className="text-xs text-muted-foreground font-mono">{flag.featureKey}</p>
              </div>
              {statusBadge(flag)}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
